import { Emitter } from "@core/events/emitter";
import type { SceneEnvironmentPatch, SceneInfo, SceneViewState } from "@models/data/sceneInfo";
import {
  UNSET_SCENE_VIEW,
  isLightVisible,
  isSceneViewResolvable,
  isSceneViewSet,
  isShadowVisible,
  sceneViewApproximately,
  vec3Approximately,
} from "@models/data/sceneInfo";
import { cameraTransitionDurationSeconds } from "@models/data/sceneDto";
import { SKYBOX_MODE } from "@models/data/skyboxData";
import type { IProjectModel } from "@models/project/IProjectModel";
import type { ISceneModel, ISceneViewCameraHost, SceneCameraPose } from "./ISceneModel";

/**
 * Unity's `SetSceneView` transition length AND the delay before
 * `FinishSceneView` runs — `var time = 0.1f;` used for both
 * (`DesktopSceneModel.cs:322-324`), so they are the same number by
 * construction rather than by coincidence.
 */
const K_SET_SCENE_VIEW_SECONDS = 0.1;

/**
 * `SceneModel` — implementation of {@link ISceneModel}.
 *
 * Unity reference: `Models/SceneModel/BaseSceneModel.cs`. The web port
 * keeps the subset of behavior the viewport needs: active-scene mirroring
 * (forwarded from `IProjectModel`), origin transform stored as plain data,
 * and the scene-environment toggles (table / console / grid / shadows /
 * default lights) with their persistence path.
 *
 * Per the rule in `models/scene/RULE.mdc`, scene transitions emit
 * `onBeginLoadScene` BEFORE any persistence flush — UI composables rely on
 * this to dismiss inspectors cleanly.
 */
export class SceneModel implements ISceneModel {
  readonly onActiveSceneChanged = new Emitter<SceneInfo | null>();
  readonly onBeginLoadScene: ISceneModel["onBeginLoadScene"] = new Emitter();
  readonly onLoadSceneCompleted = new Emitter<{ sceneId: string }>();
  readonly onSceneEnvironmentChanged = new Emitter<SceneEnvironmentPatch>();

  readonly onSceneViewApplied = new Emitter<{ sceneId: string }>();

  private readonly m_projectModel: IProjectModel;
  private m_activeScene: SceneInfo | null = null;
  private m_originPos: readonly [number, number, number] = [0, 0, 0];
  private m_originRot: readonly [number, number, number] = [0, 0, 0];

  private m_cameraHost: ISceneViewCameraHost | null = null;
  /**
   * Unity `m_defaultCameraPosition` / `m_defaultPivot` — where the camera
   * belongs for the active scene: its authored view, or the camera default
   * when it has none. Refreshed by {@link refreshDefaultPose} (Unity
   * `GetSceneView`).
   */
  private m_defaultPose: SceneCameraPose | null = null;
  /**
   * Unity `m_isCameraInitialized` — latched by the first scene activation of
   * a project so it lands INSTANTLY (Unity `OnBeginLoadScene` →
   * `ResetCamera()`), and cleared on project change (Unity
   * `OnUnloadProjectCompleted`).
   */
  private m_cameraInitialized = false;
  /**
   * Where the APP last flew the camera on purpose, when that was not the scene's
   * own view — see {@link applyTransientCameraView}. Null means "the scene's
   * view is the baseline", which is every path but that one.
   */
  private m_transientPose: SceneCameraPose | null = null;

  constructor(projectModel: IProjectModel) {
    this.m_projectModel = projectModel;
    this.m_activeScene = projectModel.activeScene;
    projectModel.onActiveSceneChanged.on((scene) => {
      // Unity computes `m_isCameraMoved` at the tail of `OnLoadSceneCompleted`
      // — which upstream fires while the OLD scene is still active — and
      // consumes it one event later in `SetSceneCamera`. The web's two events
      // are in the opposite order (`onLoadSceneCompleted` lands after the
      // build, i.e. after this), so the question is asked here instead, before
      // `m_activeScene` moves on. Same meaning, and it does not inherit
      // upstream's quirk of reusing a stale value when the target scene is
      // already resident and no load event fires at all.
      const cameraMoved = this.isCameraMoved();
      // Consumed: the baseline answers exactly one question, and past this
      // point the INCOMING scene's view is what the camera is measured against
      // again.
      this.m_transientPose = null;
      this.m_activeScene = scene;
      this.onActiveSceneChanged.emit(scene);
      this.setSceneCamera(scene, cameraMoved);
    });
    // Unity `OnUnloadProjectCompleted` clears only the initialized latch; the
    // resolved default pose survives, exactly as `m_default*` does upstream.
    projectModel.onActiveProjectChanged.on(() => {
      this.m_cameraInitialized = false;
      this.m_transientPose = null;
    });
    // Keep the local mirror fresh when the active scene's row mutates
    // (environment writes, server reconciliation, rename, …).
    projectModel.onSceneChangesApplied.on(({ sceneId }) => {
      if (this.m_activeScene?.id !== sceneId) return;
      this.m_activeScene = projectModel.getScene(sceneId);
      // Unity `OnSceneChangesApplied` (`DesktopSceneModel.cs:174-183`):
      // re-baseline the default pose when the changed scene reads as having
      // an authored view — so a peer's Set Scene View updates where OUR reset
      // button lands — but do NOT move the camera.
      const scene = this.m_activeScene;
      if (scene && isSceneViewSet(scene.sceneView)) {
        this.m_defaultPose = poseOf(scene.sceneView);
      }
    });
    projectModel.onBeginLoadScene.on((evt) => this.onBeginLoadScene.emit(evt));
    projectModel.onLoadSceneCompleted.on((evt) =>
      this.onLoadSceneCompleted.emit(evt),
    );
  }

  get activeScene(): SceneInfo | null {
    return this.m_activeScene;
  }

  get originTransform() {
    return { position: this.m_originPos, rotation: this.m_originRot };
  }

  // Unity fresh-scene defaults when no scene is active (`SceneData` field
  // initializers): table + console on, grid off, shadows on, lights on.
  get tableVisible(): boolean {
    return this.m_activeScene?.tableVisible ?? true;
  }

  get consoleVisible(): boolean {
    return this.m_activeScene?.consoleVisible ?? true;
  }

  get gridVisible(): boolean {
    return this.m_activeScene?.gridVisible ?? false;
  }

  get shadowsVisible(): boolean {
    const scene = this.m_activeScene;
    return scene ? isShadowVisible(scene.shadowsVisibleMask) : true;
  }

  get lightVisible(): boolean {
    const scene = this.m_activeScene;
    return scene ? isLightVisible(scene.lightMode) : true;
  }

  setOriginTransform(
    position: readonly [number, number, number],
    rotation: readonly [number, number, number],
  ): void {
    this.m_originPos = position;
    this.m_originRot = rotation;
  }

  async updateSceneEnvironmentAsync(env: SceneEnvironmentPatch): Promise<void> {
    const scene = this.m_activeScene;
    if (!scene) return;

    const current: SceneEnvironmentPatch = {
      shadowsVisible: isShadowVisible(scene.shadowsVisibleMask),
      gridVisible: scene.gridVisible,
      consoleVisible: scene.consoleVisible,
      tableVisible: scene.tableVisible,
      lightVisible: isLightVisible(scene.lightMode),
    };
    if (environmentEquals(current, env)) return;

    this.onSceneEnvironmentChanged.emit(env);
    await this.m_projectModel.updateSceneEnvironmentAsync(scene.id, env);
  }

  // ------------------------------------------------------------------
  // Scene view (Unity `DesktopSceneModel.cs:206-425`)
  // ------------------------------------------------------------------

  attachSceneViewCameraHost(host: ISceneViewCameraHost | null): void {
    this.m_cameraHost = host;
    this.m_transientPose = null;
    if (!host) return;
    // A viewport mounting into an already-open project has missed the
    // activation event that would have placed the camera, so place it now.
    // Unity gets this for free because the camera and the scene model share a
    // lifetime; on the web the viewport can remount under a live project.
    this.refreshDefaultPose();
    if (this.m_activeScene) {
      this.m_cameraInitialized = true;
      if (this.m_defaultPose) host.applyCameraPose(this.m_defaultPose, 0);
    }
  }

  get isSceneViewCameraAttached(): boolean {
    return this.m_cameraHost !== null;
  }

  get activeSceneView(): SceneViewState {
    return this.m_activeScene?.sceneView ?? UNSET_SCENE_VIEW;
  }

  captureSceneView(): SceneViewState | null {
    const scene = this.m_activeScene;
    const pose = this.m_cameraHost?.getCameraPose();
    if (!scene || !pose) return null;
    // Unity captures `IsOrthographic` from the live component here; the web
    // camera is perspective-only, so the scene's stored flag is carried
    // through untouched rather than being forced to false — a desktop user's
    // orthographic view survives a web Set Scene View.
    return {
      position: pose.position,
      pivot: pose.pivot,
      isOrthographic: scene.sceneView.isOrthographic,
    };
  }

  async setSceneViewAsync(): Promise<void> {
    const scene = this.m_activeScene;
    const view = this.captureSceneView();
    if (!scene || !view) return;
    await this.applySceneViewAsync(scene.id, view);
  }

  async applySceneViewAsync(sceneId: string, view: SceneViewState): Promise<void> {
    // Unity writes the three values onto BOTH `SceneInfo` and `m_default*`,
    // then — if the result does not read as an authored view — re-points
    // `m_default*` at the camera defaults while leaving the now-zeroed values
    // persisted (`DesktopSceneModel.cs:310-318`). That branch exists for UNDO:
    // reverting to a never-set view must leave the camera somewhere sane.
    this.m_defaultPose = isSceneViewSet(view)
      ? poseOf(view)
      : (this.m_cameraHost?.getDefaultPose() ?? poseOf(view));

    // Unity starts the move BEFORE the cloud write settles (its write is
    // fire-and-forget). Keep that order: the camera must not wait on S3.
    const isActive = this.m_activeScene?.id === sceneId;
    if (isActive) this.m_transientPose = null;
    if (isActive && this.m_defaultPose) {
      this.m_cameraHost?.applyCameraPose(this.m_defaultPose, K_SET_SCENE_VIEW_SECONDS);
    }

    // Unity's `Invoke(nameof(FinishSceneView), time)` fires unconditionally —
    // it has no way to know the write failed, because it never awaits it. The
    // web DOES know: `updateSceneViewAsync` rolls the local view back and
    // rethrows, and the caller turns that into an error toast. So the finish
    // leg waits for the write as well as the 0.1 s, and is skipped entirely on
    // failure. Announcing "Scene view set" beside "Scene view save failed",
    // and re-uploading a card for a view that was rolled back, is worse than
    // the extra wait. The 0.1 s is still honoured so the toast lands as the
    // camera settles rather than mid-move.
    const settled = Promise.all([
      this.m_projectModel.updateSceneViewAsync(sceneId, view),
      isActive
        ? new Promise((resolve) =>
            globalThis.setTimeout(resolve, K_SET_SCENE_VIEW_SECONDS * 1000),
          )
        : Promise.resolve(),
    ]);
    await settled;

    if (!isActive || this.m_activeScene?.id !== sceneId) return;
    // Unity `FinishSceneView`: refresh the card, then toast. The web's
    // thumbnail trigger is the debounced one every other local edit uses.
    this.m_projectModel.scheduleActiveSceneThumbnail();
    this.onSceneViewApplied.emit({ sceneId });
  }

  applyTransientCameraView(pose: SceneCameraPose, durationSeconds: number): boolean {
    const host = this.m_cameraHost;
    if (!host) return false;
    host.applyCameraPose(pose, durationSeconds);
    // Record it as the baseline BEFORE returning: `isCameraMoved` is asked at
    // the next scene change, and the question it must answer is "did the USER
    // move the camera", not "is the camera off the scene's view" — which this
    // move has just made true on purpose. Cleared by that read, and by every
    // other camera move, so it can never outlive the one question it answers.
    this.m_transientPose = pose;
    return true;
  }

  resetSceneCamera(): void {
    this.m_transientPose = null;
    this.refreshDefaultPose();
    if (this.m_defaultPose) this.m_cameraHost?.applyCameraPose(this.m_defaultPose, 0);
  }

  transitionResetSceneCamera(): void {
    this.m_transientPose = null;
    this.refreshDefaultPose();
    if (!this.m_defaultPose) return;
    // Unity reads the duration from the ACTIVE scene, falling back to Quick
    // when there is none (`DesktopSceneModel.cs:228`).
    const speed = this.m_activeScene?.transitionSpeed ?? 1;
    this.m_cameraHost?.applyCameraPose(
      this.m_defaultPose,
      cameraTransitionDurationSeconds(speed),
    );
  }

  frameActiveSceneView(): boolean {
    const host = this.m_cameraHost;
    if (!host) return false;
    this.refreshDefaultPose();
    const destination = this.m_defaultPose;
    if (!destination) return false;

    // Where the camera will BE when it stops moving: the destination of the
    // move in flight, or the live pose. A focus glide answers null and so
    // counts as elsewhere — it was never heading to a scene view.
    const landing = host.isCameraTransitionActive
      ? host.getPoseTransitionDestination?.() ?? null
      : host.getCameraPose();
    if (
      landing
      && vec3Approximately(landing.position, destination.position)
      && vec3Approximately(landing.pivot, destination.pivot)
    ) {
      // On the scene's view (or about to be): that is also the baseline the
      // next scene change's "did the user move it?" test should measure from.
      this.m_transientPose = null;
      return true;
    }
    this.transitionResetSceneCamera();
    return true;
  }

  /**
   * Port of `DesktopSceneModel.SetSceneCamera` (`:417`), driven by the active
   * scene change: refresh the destination, then go there only if the user has
   * not moved the camera away from where the OUTGOING scene said it belonged.
   */
  private setSceneCamera(scene: SceneInfo | null, cameraMoved: boolean): void {
    if (!scene || !this.m_cameraHost) return;
    this.m_transientPose = null;
    this.refreshDefaultPose();
    if (!this.m_defaultPose) return;

    if (!this.m_cameraInitialized) {
      // Unity's one-shot in `OnBeginLoadScene` (`:120-127`) runs while
      // `ActiveScene` is still NULL — `RaiseBeginLoadScene` is inside
      // `LoadSceneAsync`, and `SetActiveSceneAsync` only runs after it — so
      // its `GetSceneView()` resolves to `m_initial*` and `ResetCamera()`
      // snaps to the CAMERA DEFAULT, not to the incoming scene's view. The
      // animated move to that view then comes from `SetSceneCamera` on the
      // very next event, exactly like any other scene change. So the first
      // scene of a project reads as a glide INTO its saved view, and the port
      // must not collapse that into a snap.
      this.m_cameraInitialized = true;
      this.m_cameraHost.applyCameraPose(this.m_cameraHost.getDefaultPose(), 0);
    } else if (cameraMoved) {
      return;
    }
    this.m_cameraHost.applyCameraPose(
      this.m_defaultPose,
      cameraTransitionDurationSeconds(scene.transitionSpeed),
    );
  }

  /**
   * Port of `DesktopSceneModel.GetSceneView` (`:395`): point the default pose
   * at the active scene's authored view, or at the camera's own default when
   * the scene has none.
   *
   * ⚠ The predicate here is `isSceneViewResolvable`, NOT `isSceneViewSet` —
   * upstream really does use two different tests. This one is
   * `(position != 0 AND pivot != 0) OR orthographic` with exact equality,
   * while `IsSceneViewSet` is an OR of the two vectors with a 1 cm tolerance.
   * They disagree for a view whose pivot is exactly the world origin, which is
   * reachable (a model placed at the origin, framed and saved), and each has
   * its own upstream call sites. Do not collapse them.
   */
  private refreshDefaultPose(): void {
    const scene = this.m_activeScene;
    if (scene && isSceneViewResolvable(scene.sceneView)) {
      this.m_defaultPose = poseOf(scene.sceneView);
      return;
    }
    this.m_defaultPose = this.m_cameraHost?.getDefaultPose() ?? this.m_defaultPose;
  }

  /**
   * Port of `DesktopSceneModel.IsCameraMoved` (`:376`): has the user moved the
   * camera away from where the CURRENT scene says it belongs? Compared with
   * Battlehub's 1 cm ball (`MathHelper.Approximately`).
   *
   * A camera animation in flight counts as NOT moved, matching Unity's
   * `!isInTransition && IsCameraMoved()` — otherwise a fast run of scene
   * switches would strand the camera after the first one.
   */
  private isCameraMoved(): boolean {
    const host = this.m_cameraHost;
    const scene = this.m_activeScene;
    if (!host || !scene) return false;
    if (host.isCameraTransitionActive) return false;

    const pose = host.getCameraPose();
    if (!pose) return false;

    // A pose the APP flew to is the baseline while it stands: the camera is
    // deliberately off the scene's view, and reading that as the user having
    // moved it would strand them at the previous framing when the scene
    // changes. If they HAVE orbited since, this still reports moved — which is
    // the behaviour the guard exists for.
    const transient = this.m_transientPose;
    if (transient) {
      return (
        !vec3Approximately(pose.position, transient.position)
        || !vec3Approximately(pose.pivot, transient.pivot)
      );
    }

    const reference = isSceneViewSet(scene.sceneView)
      ? scene.sceneView
      : viewOf(host.getDefaultPose());
    // The web camera cannot be orthographic, so the live flag is read as the
    // reference's own — otherwise every scene with a desktop-authored
    // orthographic view would report "moved" forever and never re-frame.
    return !sceneViewApproximately(
      { ...viewOf(pose), isOrthographic: reference.isOrthographic },
      reference,
    );
  }

  async setDefaultEnvironmentAsync(): Promise<void> {
    const scene = this.m_activeScene;
    if (!scene) return;

    // Unity also gates on `SceneType != LargeScale`; the web port doesn't
    // model large-scale scenes yet, so the skybox mode is the only input.
    const tableVisible = scene.skybox.mode === SKYBOX_MODE.None;
    if (scene.tableVisible === tableVisible) return;

    const env: SceneEnvironmentPatch = {
      shadowsVisible: isShadowVisible(scene.shadowsVisibleMask),
      gridVisible: scene.gridVisible,
      consoleVisible: scene.consoleVisible,
      tableVisible,
      lightVisible: isLightVisible(scene.lightMode),
    };
    this.onSceneEnvironmentChanged.emit(env);
    await this.m_projectModel.updateSceneEnvironmentAsync(scene.id, env);
  }
}

function poseOf(view: SceneViewState): SceneCameraPose {
  return { position: view.position, pivot: view.pivot };
}

function viewOf(pose: SceneCameraPose): SceneViewState {
  return { position: pose.position, pivot: pose.pivot, isOrthographic: false };
}

function environmentEquals(a: SceneEnvironmentPatch, b: SceneEnvironmentPatch): boolean {
  return (
    a.shadowsVisible === b.shadowsVisible &&
    a.gridVisible === b.gridVisible &&
    a.consoleVisible === b.consoleVisible &&
    a.tableVisible === b.tableVisible &&
    a.lightVisible === b.lightVisible
  );
}
